<script setup lang="ts">
/**
 * "A newer version is out": a small panel in the corner, once, when the game
 * has been updated since this page loaded (util/version_watch.ts). "Later"
 * folds it down to a chip that stays, so it is not forgotten and never asks
 * twice. Nothing reloads unless the player presses Reload.
 *
 * In the kit's terms this is an arrival, so it is a corner panel and its
 * light runs once: the particle trace on arriving (holo_trace.ts, the same
 * one the Cell Library opens with). Nothing here loops.
 */
import { nextTick, ref, watch } from 'vue';
import { version, startVersionWatch, reloadForUpdate, readChanges, type ChangeEntry } from '../util/version_watch';
import { runPanelTrace } from '../util/holo_trace';

startVersionWatch();

const folded = ref(false);
const panelEl = ref<HTMLElement | null>(null);
// The chip's x (Nseraf, report 340): the reminder goes, the update still waits
// for the next reload.
const dismissed = ref(false);
let dismissedAt = 0;
async function dismiss() {
  dismissed.value = true;
  try { dismissedAt = (await readChanges()).since.length; } catch { dismissedAt = Infinity; }
}
const reloading = ref(false);

watch(() => version.newer, async (now, was) => {
  // Each newer version brings its own list of what changed: read it again
  // the next time it is opened, not the one from an earlier update.
  changes.value = null;
  if (showChanges.value) void loadChanges();
  // Waved away: it stays away until an update has something new for players.
  if (dismissed.value) {
    try { if ((await readChanges()).since.length > dismissedAt) dismissed.value = false; } catch { /* stays away */ }
  }
  // The first newer version opens the panel. One after that changes nothing:
  // the player has already been told, and has the chip.
  if (!now || was) return;
  // An update with no notes for players (marked [no player change]) gets only
  // the chip: opening the panel then showed the same older notes again, as if
  // they were new (Ames 2026-10-25: "it seems to be repeating").
  try {
    const { since } = await readChanges();
    if (!since.length) { folded.value = true; return; }
  } catch { /* open it as before */ }
  await nextTick();
  if (panelEl.value) runPanelTrace(panelEl.value);
  panelEl.value?.querySelector<HTMLButtonElement>('.nge-ver-go')?.focus({ preventScroll: true });
});

function reload() { reloading.value = true; reloadForUpdate(); }

// ── What changed: the i button ──────────────────────────────────────────
const showChanges = ref(false);
const changes = ref<ChangeEntry[] | null>(null);
/** True when the list is the changes since this page loaded; false when it
 *  is only the newest few, because nothing was added since the page loaded. */
const changesAreNew = ref(true);
async function loadChanges() {
  const { since, latest } = await readChanges();
  changesAreNew.value = since.length > 0;
  changes.value = since.length ? since : latest;
}
async function toggleChanges() {
  showChanges.value = !showChanges.value;
  if (!showChanges.value || changes.value) return;
  await loadChanges();
}
const dayOf = (iso: string) => { try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return ''; } };

/** The chip lives in the top bar with the other tools, first in the row, so
 *  it can never sit on top of one. Before sign in there is no such row and it
 *  floats under the bar instead. */
const inBar = ref(false);
watch([() => version.newer, folded], () => { inBar.value = !!document.querySelector('.nge-toolbar-icons'); });
</script>

<template>
  <Teleport to="body">
    <div v-if="version.newer && !folded" ref="panelEl" class="nge-ver" role="status" aria-live="polite">
      <span class="nge-ver-corner nge-ver-corner--tl" aria-hidden="true"></span>
      <span class="nge-ver-corner nge-ver-corner--br" aria-hidden="true"></span>
      <div class="nge-ver-label">
        <span class="nge-ver-pip" aria-hidden="true"></span><span class="nge-ver-label-text">New version ready</span>
        <button type="button" class="nge-ver-info" :aria-expanded="showChanges ? 'true' : 'false'" aria-controls="nge-ver-changes"
                :title="showChanges ? 'Hide what changed' : 'See what changed'" aria-label="What changed" @click="toggleChanges">i</button>
      </div>
      <div class="nge-ver-text">Pyr was updated while you were here. Reload to get the newest fixes. Your edits are already saved.</div>
      <div v-if="showChanges" id="nge-ver-changes" class="nge-ver-changes" tabindex="0" aria-label="What changed">
        <div v-if="!changes" class="nge-ver-changes-note">Reading what changed.</div>
        <div v-else-if="!changes.length" class="nge-ver-changes-note">No notes were written for this update. It is likely a small fix.</div>
        <template v-else>
          <div class="nge-ver-changes-head">{{ changesAreNew ? 'What changed' : 'Earlier changes' }}</div>
          <div v-if="!changesAreNew" class="nge-ver-changes-note">This update has no new notes: it is a fix behind the scenes. These came before it.</div>
          <div v-for="(c, i) in changes" :key="i" class="nge-ver-change">
            <div class="nge-ver-change-title">{{ c.title }}<span class="nge-ver-change-day">{{ dayOf(c.at) }}</span></div>
            <ul><li v-for="(item, j) in c.items" :key="j">{{ item }}</li></ul>
          </div>
        </template>
      </div>
      <div class="nge-ver-actions">
        <button type="button" class="nge-ver-btn nge-ver-go" :disabled="reloading" @click="reload">{{ reloading ? 'Reloading' : 'Reload now' }}</button>
        <button type="button" class="nge-ver-btn" @click="folded = true">Later</button>
      </div>
    </div>
  </Teleport>
  <Teleport v-if="version.newer && folded && !dismissed" :to="inBar ? '.nge-toolbar-icons' : 'body'">
    <!-- One pill (Ames 2026-11-02: the x sat outside the box): Reload, then x. -->
    <span class="nge-ver-chip" :class="{ 'nge-ver-chip--bar': inBar, 'nge-ver-chip--busy': reloading }">
      <button type="button" class="nge-ver-chip-go" :disabled="reloading"
              title="A newer version of Pyr is out. Click to reload and get it." @click="reload">
        <span class="nge-ver-pip" aria-hidden="true"></span>{{ reloading ? 'Reloading' : 'Update ready' }}
      </button>
      <button type="button" class="nge-ver-chip-x" title="Hide this. The update comes with your next reload." aria-label="Hide the update reminder" @click="dismiss">×</button>
    </span>
  </Teleport>
</template>

<style>
.nge-ver, .nge-ver-chip {
  --ver-line: 53, 181, 255;   /* the kit's beam blue, as holo_trace.ts draws it */
  /* Top centre: the one stretch of the screen no panel, list or menu uses. */
  position: fixed; left: 50%; z-index: 10040;
  font-family: 'Inter', system-ui, sans-serif;
  color: #dbe7f7;
}
.nge-ver {
  top: 64px; width: 340px; max-width: calc(100vw - 32px); margin-left: max(-170px, calc(-50vw + 16px)); padding: 13px 15px 12px;
  background: linear-gradient(180deg, rgba(13, 22, 40, 0.97), rgba(6, 10, 20, 0.97));
  border: 1px solid rgba(var(--ver-line), 0.34);
  border-radius: 10px;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(var(--ver-line), 0.35);
  /* Arrives once: rises a little and sharpens. */
  animation: nge-ver-in 0.42s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
@keyframes nge-ver-in {
  from { opacity: 0; transform: translateY(-10px) scale(0.985); filter: blur(3px); }
  to { opacity: 1; transform: none; filter: none; }
}
/* Corner brackets that settle outward as the panel arrives, then rest. */
.nge-ver-corner { position: absolute; width: 11px; height: 11px; border: 1px solid rgba(var(--ver-line), 0.9); pointer-events: none; animation: nge-ver-corner 0.6s 0.1s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
.nge-ver-corner--tl { top: -1px; left: -1px; border-right: 0; border-bottom: 0; border-top-left-radius: 10px; --cx: 4px; --cy: 4px; }
.nge-ver-corner--br { bottom: -1px; right: -1px; border-left: 0; border-top: 0; border-bottom-right-radius: 10px; --cx: -4px; --cy: -4px; }
@keyframes nge-ver-corner {
  from { opacity: 0; transform: translate(var(--cx), var(--cy)); }
  to { opacity: 1; transform: none; }
}
.nge-ver-label {
  display: flex; align-items: center; gap: 8px;
  font: 600 11px 'Orbitron', 'Inter', sans-serif; letter-spacing: 0.16em; text-transform: uppercase;
  color: rgb(124, 196, 255); margin-bottom: 7px;
}
.nge-ver-label-text { flex: 1; }
/* The i: a small round instrument button, lit while its list is open. */
.nge-ver-info {
  width: 19px; height: 19px; border-radius: 50%; flex-shrink: 0; padding: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  font: italic 700 12px Georgia, 'Times New Roman', serif; letter-spacing: 0; text-transform: none; line-height: 1;
  background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(var(--ver-line), 0.55); color: rgb(160, 212, 255);
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s, color 0.15s;
}
.nge-ver-info:hover, .nge-ver-info.holo-on, .nge-ver-info[aria-expanded="true"] {
  border-color: rgba(var(--ver-line), 0.95); background: rgba(var(--ver-line), 0.22); color: #fff; box-shadow: 0 0 10px rgba(var(--ver-line), 0.4);
}
.nge-ver-info:focus-visible, .nge-ver-changes:focus-visible { outline: 2px solid rgb(124, 196, 255); outline-offset: 2px; }
.nge-ver-changes {
  margin-top: 10px; padding: 9px 10px 4px; max-height: min(260px, 42vh); overflow-y: auto;
  border: 1px solid rgba(var(--ver-line), 0.22); border-radius: 8px; background: rgba(0, 0, 0, 0.28);
  /* Opens once: unfolds downward. */
  animation: nge-ver-open 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) both; transform-origin: top;
}
@keyframes nge-ver-open { from { opacity: 0; transform: scaleY(0.92); } to { opacity: 1; transform: none; } }
.nge-ver-changes-head { font: 600 9.5px 'Orbitron', 'Inter', sans-serif; letter-spacing: 0.16em; text-transform: uppercase; color: rgb(124, 196, 255); margin-bottom: 7px; }
.nge-ver-changes-note { font-size: 12px; color: #9fb0c8; padding-bottom: 5px; }
.nge-ver-change { margin-bottom: 9px; }
.nge-ver-change-title { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; font-size: 12.5px; font-weight: 600; color: #e6eefb; }
.nge-ver-change-day { font-size: 10.5px; font-weight: 400; color: #7f92ad; flex-shrink: 0; }
.nge-ver-change ul { margin: 4px 0 0; padding-left: 16px; }
.nge-ver-change li { font-size: 12px; line-height: 1.45; color: #b9c9de; margin-bottom: 3px; }
.nge-ver-change li::marker { color: rgba(var(--ver-line), 0.8); }
.nge-ver-pip { width: 7px; height: 7px; border-radius: 50%; background: rgb(var(--ver-line)); box-shadow: 0 0 8px rgba(var(--ver-line), 0.9); flex-shrink: 0; }
.nge-ver-text { font-size: 12.5px; line-height: 1.45; color: #b9c9de; }
.nge-ver-actions { display: flex; gap: 8px; margin-top: 11px; }
.nge-ver-btn {
  font: 600 10.5px 'Orbitron', 'Inter', sans-serif; letter-spacing: 0.12em; text-transform: uppercase;
  padding: 7px 12px; border-radius: 6px; cursor: pointer;
  background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.16); color: #cfdcef;
  transition: border-color 0.15s, background 0.15s, color 0.15s, box-shadow 0.15s;
}
.nge-ver-go { background: rgba(var(--ver-line), 0.18); border-color: rgba(var(--ver-line), 0.75); color: #eef7ff; }
.nge-ver-btn:hover:not(:disabled), .nge-ver-btn.holo-on { border-color: rgba(var(--ver-line), 0.9); color: #fff; }
.nge-ver-go:hover:not(:disabled), .nge-ver-go.holo-on { background: rgba(var(--ver-line), 0.3); box-shadow: 0 0 14px rgba(var(--ver-line), 0.35); }
.nge-ver-btn:focus-visible, .nge-ver-chip:focus-visible { outline: 2px solid rgb(124, 196, 255); outline-offset: 2px; }
.nge-ver-btn:disabled, .nge-ver-chip--busy { opacity: 0.6; cursor: default; }
.nge-ver-chip {
  display: inline-flex; align-items: center; gap: 7px;
  font: 600 10px 'Orbitron', 'Inter', sans-serif; letter-spacing: 0.14em; text-transform: uppercase;
  top: 58px; transform: translateX(-50%);
  padding: 6px 11px; border-radius: 14px; cursor: pointer; white-space: nowrap;
  background: rgba(8, 13, 26, 0.95); border: 1px solid rgba(var(--ver-line), 0.5);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.nge-ver-chip:hover:not(.nge-ver-chip--busy), .nge-ver-chip.holo-on { border-color: rgba(var(--ver-line), 0.95); box-shadow: 0 0 12px rgba(var(--ver-line), 0.3); }
@media (prefers-reduced-motion: reduce) {
  .nge-ver, .nge-ver-corner, .nge-ver-changes { animation: none; }
}
/* In the top bar it is one of the row's own items, first in line. */
.nge-ver-chip-go { display: inline-flex; align-items: center; gap: 7px; padding: 0; font: inherit; letter-spacing: inherit; text-transform: inherit; color: inherit; background: none; border: 0; cursor: pointer; }
.nge-ver-chip-go:disabled { cursor: default; }
.nge-ver-chip--busy { opacity: 0.6; }
.nge-ver-chip-x { margin-left: 4px; width: 16px; height: 16px; padding: 0; display: inline-flex; align-items: center; justify-content: center; font: 700 12px/1 system-ui, sans-serif; letter-spacing: 0; color: #cfe6ff; background: none; border: 0; border-radius: 50%; cursor: pointer; opacity: 0.7; }
.nge-ver-chip-x:hover { opacity: 1; background: rgba(124, 196, 255, 0.2); }
.nge-ver-chip.nge-ver-chip--bar { position: static; transform: none; order: -1; margin-right: 8px; flex-shrink: 0; }
/* The bar strips its buttons bare; this one keeps its outline and its light. */
#extensionBar .nge-ver-chip.nge-ver-chip--bar {
  display: inline-flex !important; align-items: center; gap: 7px; width: auto; height: auto;
  padding: 5px 11px !important; border-radius: 14px !important;
  background: rgba(8, 13, 26, 0.95) !important; border: 1px solid rgba(var(--ver-line), 0.55) !important;
  color: #dbe7f7 !important; font: 600 10px 'Orbitron', 'Inter', sans-serif !important; letter-spacing: 0.14em !important;
}
#extensionBar .nge-ver-chip.nge-ver-chip--bar:hover:not(.nge-ver-chip--busy),
#extensionBar .nge-ver-chip.nge-ver-chip--bar.holo-on { border-color: rgba(var(--ver-line), 0.95) !important; box-shadow: 0 0 12px rgba(var(--ver-line), 0.3); }
/* The bar's own button rules (10pt text, display block) reached the two
   buttons inside the pill: the words came out big and the light sat against
   the edge (Ames 2026-11-02). Put them back to the pill's own look. */
#extensionBar .nge-ver-chip .nge-ver-chip-go { display: inline-flex !important; align-items: center; gap: 7px; font: 600 10px 'Orbitron', 'Inter', sans-serif !important; letter-spacing: 0.14em; }
#extensionBar .nge-ver-chip .nge-ver-chip-x { display: inline-flex !important; align-items: center; justify-content: center; font: 700 12px/1 system-ui, sans-serif !important; letter-spacing: 0; }
#extensionBar .nge-ver-chip .nge-ver-pip { display: inline-block !important; width: 7px !important; height: 7px !important; border-radius: 50%; background: rgb(var(--ver-line)) !important; box-shadow: 0 0 8px rgba(var(--ver-line), 0.9); }
</style>
